import { useState, useMemo, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Box } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { LEVELS } from '../../../../constants';
import { FederalOrStateProps, Level, Step } from '../../../../models';
import { TaxPeriodRow } from '../../../EntityDetails/models';
import {
  getTableDataFromFinancialData,
  handleEditRowForEntityDetails,
  handleOnCellOrCommentBlurForEntityDetails
} from '../../../EntityDetails/utils';
import TableWithComment from '../../../TableWithComment';

const PriorYearPayments = ({
  entityId,
  currentStateId,
  failedCells,
  isFederal = true,
  stepCompletionStatus,
  tabsData
}: FederalOrStateProps) => {
  const LEVEL: Level = useMemo(() => (isFederal ? LEVELS.FEDERAL : LEVELS.STATE), [isFederal]);
  const STEP: Step = useMemo(() => 'accounts', []);
  const LevelAndStep = useMemo(() => `${LEVEL}.${STEP as string}`, [LEVEL, STEP]);

  const { t } = useTranslation();
  const [rows, setRows] = useState<TaxPeriodRow[]>([]);
  const dispatch = useDispatch();
  const { prov3322EditPerformanceFix: isPerformanceFixEnabled } = useFlags();
  const financialInfo = useMemo(() => tabsData[LevelAndStep] ?? [], [LevelAndStep, tabsData]);
  const rowNames = useMemo(
    () => [
      { name: t('Extension'), creditName: 'priorYearPayments', jurisdictionId: currentStateId },
      { name: t('Tax Return Payment'), creditName: 'priorYearPayments', jurisdictionId: currentStateId },
      { name: t('Refunds'), creditName: 'priorYearPayments', jurisdictionId: currentStateId },
      { name: t('Other Payments'), creditName: 'priorYearPayments', jurisdictionId: currentStateId }
    ],
    [currentStateId, t]
  );
  const rowsWithData = useMemo(() => getTableDataFromFinancialData(rowNames, financialInfo), [
    financialInfo,
    rowNames
  ]) as TaxPeriodRow[];

  useEffect(() => {
    setRows(rowsWithData);
  }, [rowsWithData]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Prior Year Payments')
      },
      {
        field: 'amount',
        headerName: '',
        isEditable: !stepCompletionStatus,
        isNumber: true
      }
    ],
    [stepCompletionStatus, t]
  );

  const dataForMethods = {
    columns,
    creditName: 'priorYearPayments',
    dispatch,
    entityId,
    financialInfo,
    jurisdictionId: currentStateId,
    level: LEVEL,
    rows,
    setRows,
    step: STEP,
    t
  };

  return (
    <Box style={{ overflow: 'visible' }}>
      <TableWithComment
        columns={columns}
        failedCells={failedCells}
        rows={rows}
        hideActionsMenu={stepCompletionStatus}
        totalHeaderName={t('Total Payments (Refunds)')}
        onCellChange={(params) => {
          if (!isPerformanceFixEnabled) {
            handleEditRowForEntityDetails({
              ...dataForMethods,
              ...params
            });
          }
        }}
        onCellOrCommentBlur={(params) => {
          if (isPerformanceFixEnabled) {
            handleEditRowForEntityDetails({
              ...dataForMethods,
              ...params
            });
          }

          handleOnCellOrCommentBlurForEntityDetails({
            ...dataForMethods,
            ...params
          });
        }}
      />
    </Box>
  );
};

export default PriorYearPayments;
